// components/Application/SelfTrackingApplicationModal.jsx
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import doctoralFields from '../../data/doctoral_fields.json';
import institutionOptions from '../../data/institution_options.json';
import styles from './CreatePosition.module.css';

const fieldOptions = doctoralFields.map(({ code, field }) => ({
  value: field,
  label: `${field} (${code})`,
}));

function SelfTrackingApplicationModal({ application, onSubmit, onClose }) {
  const [formData, setFormData] = useState({
    positionTitle: '',
    institution: '',
    professorName: '',
    applicationLink: '',
    description: '',
    fields: [],
    city: '',
    country: '',
    status: 'pending',
    applicationDate: '',
    deadlineDate: '',
    notes: '',
    start_semester: '',
    start_year: '',
  });
  const [customFields, setCustomFields] = useState(['', '']);
  const [customInstitution, setCustomInstitution] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (application) {
      setFormData({
        positionTitle: application.positionTitle || '',
        institution: application.institution || '',
        professorName: application.professorName || '',
        applicationLink: application.applicationLink || '',
        description: application.description || '',
        fields: application.fields || [],
        city: application.city || '',
        country: application.country || '',
        status: application.status || 'pending',
        applicationDate: application.applicationDate ? new Date(application.applicationDate).toISOString().split('T')[0] : '',
        deadlineDate: application.deadlineDate ? new Date(application.deadlineDate).toISOString().split('T')[0] : '',
        notes: application.notes || '',
        start_semester: application.start_semester || '',
        start_year: application.start_year || '',
      });
    }
  }, [application]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        positionTitle: formData.positionTitle.trim(),
        institution: formData.institution === 'Other' && customInstitution.trim() !== ''
          ? customInstitution.trim()
          : formData.institution.trim(),
        status: formData.status,
        applicationDate: formData.applicationDate || new Date().toISOString(),
        fields: formData.fields.includes('other')
          ? customFields.filter(field => field && field.trim() !== '')
          : formData.fields.filter(field => field && field.trim() !== ''),
      };

      // Add optional fields only if they have values
      if (formData.professorName && formData.professorName.trim()) {
        payload.professorName = formData.professorName.trim();
      }
      if (formData.description && formData.description.trim()) {
        payload.description = formData.description.trim();
      }
      if (formData.city && formData.city.trim()) {
        payload.city = formData.city.trim();
      }
      if (formData.country && formData.country.trim()) {
        payload.country = formData.country.trim();
      }
      if (formData.applicationLink && formData.applicationLink.trim()) {
        let link = formData.applicationLink.trim();
        if (!link.startsWith('http://') && !link.startsWith('https://')) {
          link = `https://${link}`;
        }
        payload.applicationLink = link;
      }
      if (formData.deadlineDate && formData.deadlineDate.trim()) {
        payload.deadlineDate = formData.deadlineDate;
      }
      if (formData.notes && formData.notes.trim()) {
        payload.notes = formData.notes.trim();
      }
      if (formData.start_semester && formData.start_semester.trim()) {
        payload.start_semester = formData.start_semester.trim();
      }
      if (formData.start_year && formData.start_year.trim()) {
        payload.start_year = parseInt(formData.start_year);
      }

      await onSubmit(payload);
    } catch (error) {
      console.error('Error creating self-tracking application:', error);
      let errorMessage = application ? '❌ Failed to update application' : '❌ Failed to create application';
      if (error.response?.data?.message) {
        errorMessage += ': ' + error.response.data.message;
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(previousData => ({
      ...previousData,
      [field]: value
    }));
  };

  const handleCustomFieldChange = (index, value) => {
    const updatedCustomFields = [...customFields];
    updatedCustomFields[index] = value;
    setCustomFields(updatedCustomFields);
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: "#F4F3EE",
      borderColor: "#3C6E71",
      padding: "4px"
    })
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {application ? 'Edit Self-Tracking Application' : 'Add Self-Tracking Application'}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        <div className={styles.modalBody}>
          <form onSubmit={handleSubmit} className={styles.positionForm}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Position Title *</label>
              <input
                required
                placeholder="e.g., PhD Position in Machine Learning"
                className={styles.formInput}
                value={formData.positionTitle}
                onChange={(e) => handleFieldChange('positionTitle', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Institution *</label>
              <Select
                options={institutionOptions}
                isClearable
                placeholder="Select Institution"
                className={styles.formSelect}
                value={institutionOptions.find(option => option.value === formData.institution) || null}
                onChange={(option) => handleFieldChange('institution', option?.value || '')}
                filterOption={(option, inputValue) => {
                  // Always show "Other" option when user is typing
                  if (option.value === 'Other') {
                    return inputValue.length > 0;
                  }
                  // Normal filtering for other options
                  return option.label.toLowerCase().includes(inputValue.toLowerCase());
                }}
                noOptionsMessage={({ inputValue }) => {
                  // Show "Other" option when no matches found and user has typed something
                  if (inputValue && inputValue.length > 0) {
                    return 'Type to search or select "Other"';
                  }
                  return 'No options found';
                }}
                styles={customSelectStyles}
                isDisabled={isSubmitting}
                isSearchable
              />
              {formData.institution === 'Other' && (
                <div className={styles.formGroup} style={{ marginTop: '0.5rem' }}>
                  <input
                    placeholder="Enter your institution name"
                    className={styles.formInput}
                    value={customInstitution}
                    onChange={(e) => setCustomInstitution(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>
              )}
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Professor Name</label>
              <input
                placeholder="e.g., Dr. Jane Smith"
                className={styles.formInput}
                value={formData.professorName}
                onChange={(e) => handleFieldChange('professorName', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Application Link</label>
              <input
                placeholder="https://university.edu/apply"
                className={styles.formInput}
                value={formData.applicationLink}
                onChange={(e) => handleFieldChange('applicationLink', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Position Description</label>
              <textarea
                placeholder="Brief description of the position..."
                rows={3}
                className={styles.formTextarea}
                value={formData.description}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Research Fields</label>
              <Select
                isMulti
                options={[...fieldOptions, { value: 'other', label: 'Other' }]}
                placeholder="Select research fields"
                className={styles.formSelect}
                value={fieldOptions.filter(option => formData.fields.includes(option.value)).concat(
                  formData.fields.includes('other') ? [{ value: 'other', label: 'Other' }] : []
                )}
                onChange={(options) => {
                  const values = options ? options.map(option => option.value) : [];
                  handleFieldChange('fields', values);
                }}
                filterOption={(option, inputValue) => {
                  // Always show "Other" option when user is typing
                  if (option.value === 'other') {
                    return inputValue.length > 0;
                  }
                  // Normal filtering for other options
                  return option.label.toLowerCase().includes(inputValue.toLowerCase());
                }}
                noOptionsMessage={({ inputValue }) => {
                  // Show "Other" option when no matches found and user has typed something
                  if (inputValue && inputValue.length > 0) {
                    return 'Type to search or select "Other"';
                  }
                  return 'No options found';
                }}
                styles={customSelectStyles}
                isDisabled={isSubmitting}
                isSearchable
              />
            </div>
            {formData.fields.includes('other') && (
              <div className={styles.customFieldsSection}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Custom Field 1</label>
                  <input
                    placeholder="Enter custom research field"
                    className={styles.formInput}
                    value={customFields[0]}
                    onChange={(e) => handleCustomFieldChange(0, e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Custom Field 2</label>
                  <input
                    placeholder="Enter custom research field"
                    className={styles.formInput}
                    value={customFields[1]}
                    onChange={(e) => handleCustomFieldChange(1, e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            )}
            <div className={styles.locationGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>City</label>
                <input
                  placeholder="e.g., Boston"
                  className={styles.formInput}
                  value={formData.city}
                  onChange={(e) => handleFieldChange('city', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Country</label>
                <input
                  placeholder="e.g., United States"
                  className={styles.formInput}
                  value={formData.country}
                  onChange={(e) => handleFieldChange('country', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Start Semester</label>
              <select
                className={styles.formInput}
                value={formData.start_semester}
                onChange={(e) => handleFieldChange('start_semester', e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Select semester</option>
                <option value="Fall">Fall</option>
                <option value="Spring">Spring</option>
                <option value="Summer">Summer</option>
              </select>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Start Year</label>
              <select
                className={styles.formInput}
                value={formData.start_year}
                onChange={(e) => handleFieldChange('start_year', e.target.value)}
                disabled={isSubmitting}
              >
                <option value="">Select year</option>
                {Array.from({ length: 6 }, (_, i) => {
                  const year = new Date().getFullYear() + i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
            <div className={styles.locationGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Application Status *</label>
                <select
                  className={styles.formInput}
                  value={formData.status}
                  onChange={(e) => handleFieldChange('status', e.target.value)}
                  disabled={isSubmitting}
                  required
                >
                  <option value="pending">Pending</option>
                  <option value="interview">Interview</option>
                  <option value="waitlist">Waitlist</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Application Date</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={formData.applicationDate}
                  onChange={(e) => handleFieldChange('applicationDate', e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Application Deadline</label>
              <input
                type="date"
                className={styles.formInput}
                value={formData.deadlineDate}
                onChange={(e) => handleFieldChange('deadlineDate', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Notes</label>
              <textarea
                placeholder="Any additional notes about this application..."
                rows={3}
                className={styles.formTextarea}
                value={formData.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.formActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? (application ? 'Updating...' : 'Creating...') : (application ? 'Update Application' : 'Add Application')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SelfTrackingApplicationModal;